import { CloseOutlined, LockOutlined } from '@ant-design/icons';
import { Badge, Button, Card, Switch, Tag } from 'antd';
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AclDevToolsContext, type AclCheck, type AclCheckEntry } from '@/core/acl/AclDevToolsContext.js';
import { useAcl } from '@/core/acl/useAcl.js';

interface HoverInfo {
  x: number;
  y: number;
  checks: AclCheck[];
  result: boolean;
}

function parseChecks(raw: string | undefined): AclCheck[] {
  if (!raw) return [];
  try {
    return JSON.parse(raw) as AclCheck[];
  } catch {
    return [];
  }
}

function applyHighlight(el: HTMLElement | null, result: boolean) {
  if (!el) return;
  el.style.outline = `2px solid ${result ? '#52c41a' : '#ff4d4f'}`;
  el.style.outlineOffset = '2px';
}

function clearHighlight(el: HTMLElement | null) {
  if (!el) return;
  el.style.outline = '';
  el.style.outlineOffset = '';
}

function firstChild(entry: AclCheckEntry): HTMLElement | null {
  const w = entry.wrapperRef.current;
  if (!w) return null;
  return (w.firstElementChild as HTMLElement | null) ?? w;
}

export function AclDevToolsPanel() {
  const ctx = useContext(AclDevToolsContext);
  const { isSuperAdmin } = useAcl();

  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
  const outlinedEls = useRef<Set<HTMLElement>>(new Set());
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipPos, setTooltipPos] = useState({ left: 0, top: 0 });

  // Clamp tooltip inside viewport so it doesn't clip at screen edges
  useLayoutEffect(() => {
    if (!hoverInfo) return;
    const el = tooltipRef.current;
    if (!el) return;
    const { offsetWidth: w, offsetHeight: h } = el;
    const margin = 8;
    let left = hoverInfo.x + 14;
    let top = hoverInfo.y - 10;
    if (left + w + margin > window.innerWidth) left = hoverInfo.x - 14 - w;
    if (top + h + margin > window.innerHeight) top = hoverInfo.y - 10 - h;
    if (top < margin) top = margin;
    if (left < margin) left = margin;
    setTooltipPos({ left, top });
  }, [hoverInfo]);

  // Tooltip on hover over real page elements
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const gate = (e.target as HTMLElement).closest?.('[data-acl-gate]') as HTMLElement | null;
      if (gate) {
        setHoverInfo({
          x: e.clientX,
          y: e.clientY,
          checks: parseChecks(gate.dataset.aclChecks),
          result: gate.dataset.aclResult === 'true',
        });
      } else {
        setHoverInfo(null);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', () => setHoverInfo(null));
    return () => document.removeEventListener('mousemove', onMove);
  }, []);

  // Highlight all gated elements
  useEffect(() => {
    outlinedEls.current.forEach(clearHighlight);
    outlinedEls.current.clear();

    if (!ctx?.highlightAll) return;

    ctx.entries.forEach(entry => {
      const el = firstChild(entry);
      if (!el) return;
      applyHighlight(el, entry.result);
      outlinedEls.current.add(el);
    });

    return () => {
      outlinedEls.current.forEach(clearHighlight);
      outlinedEls.current.clear();
    };
  }, [ctx?.highlightAll, ctx?.entries]);

  // Hotkey: Ctrl+Shift+A
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') ctx?.toggleOpen();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [ctx?.toggleOpen]);

  // Guards AFTER all hooks — Rules of Hooks: hook order must be stable across renders
  if (!ctx) return null;
  if (!import.meta.env.DEV && !isSuperAdmin) return null;

  const { entries, isOpen, toggleOpen, highlightAll, setHighlightAll } = ctx;
  const allowed = entries.filter(e => e.result).length;
  const denied = entries.filter(e => !e.result).length;

  return createPortal(
    <>
      {hoverInfo && (
        <div
          ref={tooltipRef}
          style={{
            position: 'fixed',
            left: tooltipPos.left,
            top: tooltipPos.top,
            background: '#141414',
            color: '#fff',
            padding: '3px 8px',
            borderRadius: 4,
            fontSize: 11,
            pointerEvents: 'none',
            zIndex: 10001,
            fontFamily: 'monospace',
            border: `1px solid ${hoverInfo.result ? '#52c41a' : '#ff4d4f'}`,
            whiteSpace: 'nowrap',
          }}
        >
          {hoverInfo.checks.map((c, i) => (
            <div key={i}>
              <span style={{ color: c.result ? '#52c41a' : '#ff4d4f', marginRight: 4 }}>
                {c.result ? '✓' : '✗'}
              </span>
              {c.resource}:{c.action}
            </div>
          ))}
        </div>
      )}

      <div style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 10000 }}>
        <Badge count={denied} color="#ff4d4f" offset={[-2, 2]}>
          <Button
            type={isOpen ? 'primary' : 'default'}
            shape="circle"
            size="small"
            icon={<LockOutlined />}
            onClick={toggleOpen}
            title="ACL DevTools (Ctrl+Shift+A)"
          />
        </Badge>
      </div>

      {isOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: 52,
            right: 16,
            width: 340,
            maxHeight: 480,
            zIndex: 10000,
            boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <Card
            size="small"
            title={
              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                ACL
                <Tag color="green" style={{ marginLeft: 8, fontSize: 11 }}>{allowed} ✓</Tag>
                <Tag color="red" style={{ fontSize: 11 }}>{denied} ✗</Tag>
              </span>
            }
            extra={
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: '#888' }}>highlight</span>
                <Switch size="small" checked={highlightAll} onChange={setHighlightAll} />
                <Button size="small" type="text" icon={<CloseOutlined />} onClick={toggleOpen} />
              </div>
            }
            styles={{ body: { padding: 0, maxHeight: 400, overflowY: 'auto' } }}
          >
            {entries.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#888', fontSize: 12 }}>
                No permission gates on this page
              </div>
            ) : (
              <table
                style={{ width: '100%', fontSize: 12, fontFamily: 'monospace', borderCollapse: 'collapse' }}
              >
                <tbody>
                  {entries.map(entry => (
                    <tr
                      key={entry.id}
                      style={{ cursor: 'pointer', borderBottom: '1px solid rgba(0,0,0,0.06)' }}
                      onMouseEnter={() => {
                        const el = firstChild(entry);
                        if (el) { el.style.outline = '2px solid orange'; el.style.outlineOffset = '2px'; }
                      }}
                      onMouseLeave={() => {
                        const el = firstChild(entry);
                        if (!el) return;
                        if (highlightAll) applyHighlight(el, entry.result);
                        else clearHighlight(el);
                      }}
                      onClick={() => firstChild(entry)?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                    >
                      <td style={{ padding: '5px 8px', width: 20, textAlign: 'center', verticalAlign: 'top' }}>
                        <span style={{ color: entry.result ? '#52c41a' : '#ff4d4f', fontSize: 13 }}>
                          {entry.result ? '✓' : '✗'}
                        </span>
                      </td>
                      <td colSpan={2} style={{ padding: '5px 8px 5px 4px' }}>
                        {entry.checks.map((c, i) => (
                          <div key={i} style={{ color: c.result ? undefined : '#ff4d4f' }}>
                            <span style={{ color: '#1677ff', fontWeight: 500 }}>{c.resource}</span>
                            <span style={{ color: '#666' }}>:{c.action}</span>
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      )}
    </>,
    document.body,
  );
}
